/**
 * Shutdown Coordinator
 *
 * Provides a single, ordered beforeunload/pagehide handler for all services
 * that need to clean up IndexedDB, WebSocket, or awareness state on page exit.
 *
 * Shutdown runs in three deterministic phases:
 *   1. flush    — persist any pending writes (e.g. debounced saves)
 *   2. awareness — clear collaborative presence so peers see us leave
 *   3. destroy  — tear down providers, close connections
 *
 * Services register handlers at construction time and the coordinator
 * executes them in phase order during beforeunload.
 */

import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('SHUTDOWN_COORDINATOR');

export type ShutdownPhase = 'flush' | 'awareness' | 'destroy';

interface ShutdownHandler {
  name: string;
  phase: ShutdownPhase;
  handler: () => void;
}

const PHASE_ORDER: ShutdownPhase[] = ['flush', 'awareness', 'destroy'];

class ShutdownCoordinator {
  private handlers: ShutdownHandler[] = [];
  private installed = false;
  private executed = false;

  /**
   * Register a handler to run during page shutdown.
   * Handlers within the same phase run in registration order.
   */
  register(handler: ShutdownHandler): void {
    this.handlers.push(handler);
    this.ensureInstalled();
  }

  /**
   * Remove a previously registered handler by name.
   */
  unregister(name: string): void {
    this.handlers = this.handlers.filter(h => h.name !== name);
  }

  private ensureInstalled(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;

    const onShutdown = () => this.executeShutdown();

    // Both events are registered because mobile Safari only fires pagehide.
    // The `executed` guard prevents double-execution when both fire.
    window.addEventListener('beforeunload', onShutdown);
    window.addEventListener('pagehide', onShutdown);
  }

  private executeShutdown(): void {
    if (this.executed) return;
    this.executed = true;

    log.info('[ShutdownCoordinator] Page unloading — running shutdown handlers');

    for (const phase of PHASE_ORDER) {
      const phaseHandlers = this.handlers.filter(h => h.phase === phase);
      for (const h of phaseHandlers) {
        try {
          h.handler();
        } catch (error) {
          log.error(`[ShutdownCoordinator] Error in ${h.name} (${phase}):`, error);
        }
      }
    }

    log.info('[ShutdownCoordinator] Shutdown complete');
  }
}

export const shutdownCoordinator = new ShutdownCoordinator();
